import type { InspectorAvailabilityResult } from './types';

import { memo } from 'react';

import { Box, Chip, TableCell, TableRow, Typography } from '@mui/material';

import { UserIcon } from 'src/components/user-icon';

const bodyCellSx = {
  px: 2,
  py: 1,
  borderBottom: '1px solid',
  borderColor: 'divider',
  verticalAlign: 'middle',
};

interface InspectorAvailabilityGridRowProps {
  result: InspectorAvailabilityResult;
  showChanges?: boolean;
  onDateClick?: (result: InspectorAvailabilityResult) => void;
}

export const InspectorAvailabilityGridRow = memo(
  ({ result, showChanges = false, onDateClick }: InspectorAvailabilityGridRowProps) => (
    <TableRow hover sx={{ cursor: onDateClick ? 'pointer' : 'default' }}>
      <TableCell sx={{ ...bodyCellSx, width: 240, maxWidth: 240 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
          <UserIcon id={result.id} name={result.name} type={result.type} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" noWrap>
              {result.name}
            </Typography>
            {result.subtitle ? (
              <Typography variant="body2" color="text.secondary" noWrap>
                {result.subtitle}
              </Typography>
            ) : null}
          </Box>
        </Box>
      </TableCell>

      <TableCell sx={{ ...bodyCellSx }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {(result.competencies ?? []).map((competency) => (
            <Chip key={competency} label={competency} size="small" variant="soft" />
          ))}
        </Box>
      </TableCell>

      <TableCell sx={{ ...bodyCellSx, width: 80, maxWidth: 80 }}>
        <Box>
          <Typography variant="body2">{result.travelLabel ?? '-'}</Typography>
          {result.travelSubLabel ? (
            <Typography variant="caption" color="text.secondary">
              {result.travelSubLabel}
            </Typography>
          ) : null}
        </Box>
      </TableCell>

      <TableCell sx={{ ...bodyCellSx, width: 80, maxWidth: 80 }}>
        <Typography variant="body2" noWrap>
          {result.businessUnit ?? '-'}
        </Typography>
      </TableCell>

      {showChanges ? (
        <TableCell sx={{ ...bodyCellSx, width: 80, maxWidth: 80 }}>
          <Chip
            label={result.changesCount ?? 0}
            size="small"
            color="default"
            variant="soft"
            sx={{ borderRadius: '50%', minWidth: 28, height: 28 }}
          />
        </TableCell>
      ) : null}

      <TableCell
        sx={{ ...bodyCellSx, width: 100, maxWidth: 100 }}
        onClick={() => onDateClick?.(result)}
      >
        <Typography variant="body2">{result.dateLabel ?? '-'}</Typography>
      </TableCell>

      <TableCell sx={{ ...bodyCellSx, width: 120, maxWidth: 120 }} />
    </TableRow>
  )
);

InspectorAvailabilityGridRow.displayName = 'InspectorAvailabilityGridRow';
